import { RefreshCw, X } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";

import { PhotoUpload, VoiceRecorder } from "@/components/common";
import { Button, Textarea } from "@/components/ui";
import { CONTENT_KEYWORDS } from "@/constants/content";
import { apiAvatar } from "@/services/api-client/avatar";
import type { Avatar } from "@/types/avatar";

interface Props {
  avatar: Avatar;
  onClose: () => void;
  onRegenerated: () => void;
}

const ARCHETYPE_LABELS: Record<string, string> = {
  professional: "The Professional",
  creative: "The Creative",
  mentor: "The Mentor",
  scholar: "The Scholar",
  cyber: "The Cyber",
  // legacy
  motivational_speaker: "The Mentor",
  tech_expert: "The Professional",
  storyteller: "The Creative",
  news_anchor: "The Professional",
  comedy: "The Creative",
};

export function AvatarDetailModal({ avatar, onClose, onRegenerated }: Props) {
  const [showRegen, setShowRegen] = useState(false);
  const [regenMode, setRegenMode] = useState<"photo" | "prompt">("photo");
  const [newImage, setNewImage] = useState<string | null>(null);
  const [newPrompt, setNewPrompt] = useState("");
  const [voiceAudio, setVoiceAudio] = useState<string | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const [showSheet, setShowSheet] = useState(false);
  const [showPortrait, setShowPortrait] = useState(false);

  const handleRegenerate = async () => {
    const imageOrPrompt =
      regenMode === "photo" ? newImage : newPrompt ? `prompt:${newPrompt}` : null;
    if (!imageOrPrompt) return;

    setRegenerating(true);
    try {
      // Create new avatar with same name/archetype but new image
      await apiAvatar.createAvatar({
        name: avatar.name,
        image: imageOrPrompt,
        archetype: avatar.archetype,
        voiceAudio: voiceAudio || undefined,
      });
      toast.success("Regenerating avatar...");
      onRegenerated();
    } catch (err: any) {
      toast.error(err.message || "Failed to regenerate");
    } finally {
      setRegenerating(false);
    }
  };

  const archetypeLabel = avatar.archetype?.startsWith(CONTENT_KEYWORDS.CUSTOM_ARCHETYPE)
    ? avatar.archetype.slice(CONTENT_KEYWORDS.CUSTOM_ARCHETYPE.length).trim()
    : ARCHETYPE_LABELS[avatar.archetype] ||
      avatar.archetype?.replace(/_/g, " ") ||
      "Custom";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-overlay backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="mx-4 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with image */}
        <div className="relative">
          {avatar.image_url ? (
            <div
              className="flex cursor-zoom-in items-center justify-center rounded-t-2xl bg-zinc-100"
              style={{ height: "320px" }}
              onClick={() => setZoomed(true)}
            >
              <img
                src={avatar.image_url}
                alt={avatar.name}
                className="max-h-full max-w-full object-contain"
              />
            </div>
          ) : (
            <div className="flex h-48 items-center justify-center rounded-t-2xl bg-zinc-100">
              <span className="text-zinc-400">No image</span>
            </div>
          )}
          <Button
            variant="secondary"
            size="icon"
            onClick={onClose}
            className="absolute top-3 right-3 rounded-full"
          >
            <X />
          </Button>

          {avatar.status === "generating" && (
            <div className="absolute bottom-3 left-3 rounded-lg bg-primary px-3 py-1.5 font-semibold text-white text-xs backdrop-blur-sm">
              Generating...
            </div>
          )}
          {avatar.status === "failed" && (
            <div className="absolute bottom-3 left-3 rounded-lg bg-red-600/90 px-3 py-1.5 font-semibold text-white text-xs backdrop-blur-sm">
              Generation failed
            </div>
          )}
        </div>

        {/* Info */}
        <div className="p-5">
          <h2 className="font-bold text-xl text-zinc-900">{avatar.name}</h2>
          <p className="mt-1 text-sm text-zinc-500">{archetypeLabel}</p>
          {avatar.voiceId && (
            <p className="mt-1 text-xs text-zinc-400">Voice ID: {avatar.voiceId}</p>
          )}

          {/* Character Sheet & Portrait */}
          {(avatar.character_sheet_url || avatar.portrait_url) && (
            <div className="mt-3 flex gap-2">
              {avatar.character_sheet_url && (
                <Button
                  variant="outline"
                  onClick={() => setShowSheet(true)}
                  className="flex-1"
                >
                  🎭 Body Sheet
                </Button>
              )}
              {avatar.portrait_url && (
                <Button
                  variant="outline"
                  onClick={() => setShowPortrait(true)}
                  className="flex-1"
                >
                  📸 Face Portrait
                </Button>
              )}
            </div>
          )}

          {/* Regenerate section */}
          {!showRegen ? (
            <Button
              variant="outline"
              onClick={() => setShowRegen(true)}
              className="mt-4 w-full"
            >
              <RefreshCw />
              Regenerate with new photo/prompt
            </Button>
          ) : (
            <div className="mt-4 space-y-3 rounded-xl border border-zinc-200 p-4">
              <div className="flex gap-2">
                <Button
                  variant={regenMode === "photo" ? "default" : "outline"}
                  onClick={() => setRegenMode("photo")}
                  className="flex-1"
                >
                  📷 New Photo
                </Button>
                <Button
                  variant={regenMode === "prompt" ? "default" : "outline"}
                  onClick={() => setRegenMode("prompt")}
                  className="flex-1"
                >
                  ✏️ Text Prompt
                </Button>
              </div>

              {regenMode === "photo" ? (
                <PhotoUpload
                  label="New Photo"
                  values={newImage ? [newImage] : []}
                  onChange={(vals) => setNewImage(vals[0] ?? null)}
                  hint="Upload a new reference photo"
                />
              ) : (
                <div>
                  <label className="mb-1 block font-medium text-sm text-zinc-700">
                    Describe your character
                  </label>
                  <Textarea
                    value={newPrompt}
                    onChange={(e) => setNewPrompt(e.target.value)}
                    placeholder="e.g. A confident young woman with red hair, professional look"
                  />
                </div>
              )}

              <VoiceRecorder value={voiceAudio} onChange={setVoiceAudio} />

              <div className="flex gap-2">
                <Button
                  variant="default"
                  onClick={handleRegenerate}
                  className="flex-1"
                  disabled={
                    regenerating ||
                    (regenMode === "photo" ? !newImage : !newPrompt.trim())
                  }
                >
                  {regenerating ? "Regenerating..." : "Regenerate Avatar"}
                </Button>
                <Button variant="outline" onClick={() => setShowRegen(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Character Sheet overlay */}
      {showSheet && avatar.character_sheet_url && (
        <div
          className="fixed inset-0 z-[60] flex cursor-zoom-out items-center justify-center bg-black/90 p-4"
          onClick={() => setShowSheet(false)}
        >
          <img
            src={avatar.character_sheet_url}
            alt="Character Sheet"
            className="max-h-[95vh] max-w-[95vw] rounded-lg object-contain shadow-2xl"
          />
        </div>
      )}

      {/* Portrait overlay */}
      {showPortrait && avatar.portrait_url && (
        <div
          className="fixed inset-0 z-[60] flex cursor-zoom-out items-center justify-center bg-black/90 p-4"
          onClick={() => setShowPortrait(false)}
        >
          <img
            src={avatar.portrait_url}
            alt="Face Portrait"
            className="max-h-[95vh] max-w-[95vw] rounded-lg object-contain shadow-2xl"
          />
        </div>
      )}

      {/* Zoomed image overlay */}
      {zoomed && avatar.image_url && (
        <div
          className="fixed inset-0 z-[60] flex cursor-zoom-out items-center justify-center bg-black/90"
          onClick={() => setZoomed(false)}
        >
          <img
            src={avatar.image_url}
            alt={avatar.name}
            className="max-h-[95vh] max-w-[95vw] rounded-lg object-contain shadow-2xl"
          />
        </div>
      )}
    </div>
  );
}
